// pages/Construction/FormPage.tsx（ファイル名を変更）
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUser } from '../../hooks/useUser'; 
import {
  Container,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from '@mui/material';
import { ConstructionForm } from '../../components/Construction/ConstructionForm';
import { constructionService } from '../../services/construction';
import { customerService } from '../../services/customer';
import { memberService } from '../../services/member';
import { buildMemberShortName, findMemberBySavedName } from '../../utils/memberDisplay';
import type { Customer } from '../../types/customer';
import type { Member } from '../../types/member';
import {
  ConstructionFormData,
  APIExpense,
  APIVendor,
  APIRemark
} from '../../types/construction';
import type { RouteInfo } from '../../types/address';

const ConstructionFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { userInfo } = useUser();  // useUserフックを追加
  const [loading, setLoading] = useState(false);
  const [initialData, setInitialData] = useState<ConstructionFormData | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [routeInfo, setRouteInfo] = useState<RouteInfo | null>(null);
  const [newCustomerDialogOpen, setNewCustomerDialogOpen] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    Customer_Name: '',
    Address: ''
  });

  // 初期データの取得
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const [customersData, membersData] = await Promise.all([
          customerService.getAllCustomers(),
          memberService.getAllMembers()
        ]);
        setCustomers(customersData);
        setMembers(membersData);

        // 編集モードの場合、工事データも取得
        if (id) {
          const constructionData = await constructionService.getById(parseInt(id));
          
          // 顧客名と監督者名から該当するデータを検索
          const customer = customersData.find(c => c.Customer_Name === constructionData.customerName);
          const member = findMemberBySavedName(constructionData.supervisor ?? '', membersData);
          
          // setInitialData({
          //   ...constructionData,
          //   expenses: [],
          //   vendors: [],
          //   remarks: []
          // });
          // FormPage.tsx内のsetInitialData部分を修正
          setInitialData(constructionData);
          
          setSelectedCustomer(customer || null);
          setSelectedMember(member || null);
        }
      } catch (error) {
        console.error('初期データの取得に失敗:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchInitialData();
  }, [id]);

  const handleSubmit = async (data: ConstructionFormData) => {
    try {
      setLoading(true);
      if (!selectedCustomer?.ID || !selectedMember?.ID) {
        throw new Error('顧客と監督者を選択してください');
      }

      const constructionData = {
        ...data,
        customerId: selectedCustomer.ID,
        customerName: selectedCustomer.Customer_Name,
        supervisorId: selectedMember.ID,
        supervisor: buildMemberShortName(selectedMember, members),
        supervisorInitial: selectedMember.staff_initial,
        inputBy: userInfo?.lastName  // Input_Byを追加
      };

      if (id) {
        // 編集モード
        await constructionService.update(parseInt(id), constructionData);
        navigate('/construction', {
          state: { message: '工事データを更新しました' }
        });
      } else {
        // 新規作成モード
        await constructionService.createWithRelatedData(
          constructionData,
          data.expenses,
          data.vendors,
          data.remarks
        );
        navigate('/construction', {
          state: { message: '工事データを登録しました' }
        });
      }
    } catch (error) {
      console.error(id ? '更新エラー:' : '登録エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCustomer = async () => {
    try {
      const ID = await customerService.createCustomer(newCustomer);
      const newCustomerWithId = { ...newCustomer, ID };
      setCustomers([...customers, newCustomerWithId]);
      setSelectedCustomer(newCustomerWithId);
      setNewCustomerDialogOpen(false);
      setNewCustomer({ Customer_Name: '', Address: '' });
    } catch (error) {
      console.error('顧客の追加に失敗:', error);
    }
  };

  return (
    <Container maxWidth="md">
      <Typography variant="h5" gutterBottom sx={{ my: 3 }}>
        {id ? '工事データ編集' : '工事データ登録'}
      </Typography>

      <ConstructionForm
        initialData={initialData}  // 追加
        mode={id ? 'edit' : 'create'}  // 追加
        onSubmit={handleSubmit}
        onCancel={() => navigate('/construction')}
        loading={loading}
        customers={customers}
        members={members}
        selectedCustomer={selectedCustomer}
        selectedMember={selectedMember}
        onCustomerChange={setSelectedCustomer}
        onMemberChange={setSelectedMember}
        onNewCustomerClick={() => setNewCustomerDialogOpen(true)}
        setRouteInfo={setRouteInfo}
      />

      <Dialog 
        open={newCustomerDialogOpen} 
        onClose={() => setNewCustomerDialogOpen(false)}
      >
        <DialogTitle>新規顧客の追加</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="顧客名"
            fullWidth
            value={newCustomer.Customer_Name}
            onChange={(e) => setNewCustomer({
              ...newCustomer,
              Customer_Name: e.target.value
            })}
          />
          <TextField
            margin="dense"
            label="住所"
            fullWidth
            value={newCustomer.Address}
            onChange={(e) => setNewCustomer({
              ...newCustomer,
              Address: e.target.value
            })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewCustomerDialogOpen(false)}>
            キャンセル
          </Button>
          <Button onClick={handleAddCustomer}>
            追加
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ConstructionFormPage;